import {
  BriefcaseBusiness,
  Check,
  ChevronDown,
  Sparkles,
  Target,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSidebar } from "@/components/ui/sidebar";
import { useProductScope } from "@/context/ProductScopeContext";
import type { SidebarNavGroupId } from "@/config/sidebar-nav";
import { cn } from "@/lib/utils";

const PRODUCT_ICONS: Record<SidebarNavGroupId, LucideIcon> = {
  coach: Sparkles,
  recruit: BriefcaseBusiness,
  leads: Target,
};

/**
 * Product switcher (GRA-78): Coach, Recruit and Leads as separate
 * workspaces. Went through two prior shapes that both read as wrong against
 * the org/team switcher directly above it: first a second dropdown styled
 * like an identical twin of the org switcher's own card, then a segmented
 * tab strip that looked cramped with three icon+label pills competing for
 * width. This is a single flyout menu (same DropdownMenu primitive as
 * OrgScopeSwitcher) behind a deliberately lightweight, borderless trigger —
 * a small icon tile, the product name and a chevron, no card — so it reads
 * as its own distinct control rather than a duplicate of the boxed org
 * switcher. The menu itself lists every entitled product with an icon, name
 * and one-line description, the active one checked, modelled on how
 * multi-product tools like Lemlist present an app switcher. Hidden entirely
 * for a single-product business — there's nothing to switch between.
 */
export function ProductSwitcher() {
  const { isMobile } = useSidebar();
  const { t: tNav } = useTranslation("nav");
  const { availableProducts, activeProduct, switchProduct } =
    useProductScope();

  if (availableProducts.length <= 1) return null;

  const ActiveIcon = PRODUCT_ICONS[activeProduct];
  const activeLabel = tNav(`groups.${activeProduct}`);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="product-switcher"
          aria-label={tNav("productSwitcher.label")}
          className={cn(
            "flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-left outline-none ring-sidebar-ring transition-colors hover:bg-sidebar-accent focus-visible:ring-2 data-[state=open]:bg-sidebar-accent",
            "group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0",
          )}
        >
          <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <ActiveIcon className="size-3.5" aria-hidden="true" />
          </div>
          <span className="truncate text-sm font-semibold text-foreground group-data-[collapsible=icon]:hidden">
            {activeLabel}
          </span>
          <ChevronDown
            className="ml-auto size-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden"
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-64 rounded-lg"
        side={isMobile ? "bottom" : "right"}
        align="start"
        sideOffset={4}
      >
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {tNav("productSwitcher.label")}
        </DropdownMenuLabel>
        {availableProducts.map((product) => {
          const Icon = PRODUCT_ICONS[product];
          const isActive = product === activeProduct;
          const label = tNav(`groups.${product}`);
          const description = tNav(`productSwitcher.descriptions.${product}`);
          return (
            <DropdownMenuItem
              key={product}
              role="menuitemradio"
              aria-checked={isActive}
              data-testid={`product-switcher-item-${product}`}
              onSelect={() => switchProduct(product)}
              className={cn("gap-3 py-2.5", isActive && "bg-accent")}
            >
              <div
                className={cn(
                  "flex size-8 shrink-0 items-center justify-center rounded-md",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground",
                )}
                aria-hidden="true"
              >
                <Icon className="size-4 shrink-0" />
              </div>
              <div className="grid min-w-0 flex-1 text-left leading-tight">
                <span className="truncate text-sm font-semibold">
                  {label}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {description}
                </span>
              </div>
              {isActive && (
                <Check className="ml-auto size-4 shrink-0" aria-hidden="true" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
